import { CircularProgress, Dialog, DialogContent, DialogTitle } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { dispatch } from '#app/App';
import ButtonComponent from '#app/Component/ButtonComponent';
import SelectInput from '#app/Component/SelectInput';
import ApiHandler from '#app/Handler/ApiHandler';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import DisputeInvoice from '#app/View/Sale/OrderClient/SaleOrderClient/components/DisputeModal/DisputeInvoice';
import DisputeOrder from '#app/View/Sale/OrderClient/SaleOrderClient/components/DisputeModal/DisputeOrder';
import DisputeProduct from '#app/View/Sale/OrderClient/SaleOrderClient/components/DisputeModal/DisputeProduct';
import { ByProduct } from '#app/typings/order';
import { DisputeState, DisputeType, DisputeTypeTranslations } from '#app/typings/orderClientLitige';
import { OrderDocument, OrderDocumentType } from '#app/typings/orderDocument';

type Props = {
  onClose: () => void;
  products: ByProduct[];
};

export type Payload = {
  reason: string;
  orderDocument?: number;
  products?: {
    byProduct: number;
    quantity: number;
  }[];
  refundCarrier?: boolean;
  refundPayment?: boolean;
};

const DisputeModal: React.FC<Props> = ({ onClose, products }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid as string;
  const [type, setType] = useState<DisputeType>();
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [documents, setDocuments] = useState<OrderDocument[]>([]);

  useEffect(() => {
    ApiHandler.get<OrderDocument[]>(
      {
        route: 'api_order_document_list',
        params: { uuid },
        data: { type: OrderDocumentType.INVOICE_ORDER_CLIENT },
      },
      (response) => {
        setDocuments(response.data ?? []);
        setLoading(false);
      },
    );
  }, []);

  const onSubmit = (data: Payload): void => {
    setLoadingSubmit(true);
    ApiHandler.post<{ uuid: string }>(
      {
        route: 'api_sale_order_client_litige',
        data: { type, ...data },
        params: { uuid },
      },
      (response) => {
        const success = response.status === 200;
        const errorMessage = response.error?.message || "Une erreur inattendue s'est produite.";

        dispatch(
          SnackbarOpen({
            text: success ? 'Litige créé avec succés.' : errorMessage,
            variant: success ? 'success' : 'error',
          }),
        );

        if (success) {
          navigate(getRoutePathname('client_service_order_client_litige', { uuid: response.data?.uuid as string }));
        }
        setLoadingSubmit(false);
      },
    );
  };

  const disputeOrderProducts = products.filter(
    (product) => product.amount - product.amountShipped - product.amountLitige > 0,
  );
  const disputeInvoiceDocuments = documents.filter((document) =>
    document.orderClientLitiges.every(
      (orderClientLitige) =>
        [DisputeType.BUG_LABEL, DisputeType.SHIPPING_FEES, DisputeType.PROMO_CODE].includes(orderClientLitige.type) ||
        orderClientLitige.state === DisputeState.ARCHIVED,
    ),
  );

  const disputeProductDocuments = documents.map((document) => {
    if (document.orderClientLitiges.length === 0) {
      return document;
    }

    const itemsDispute = document.orderClientLitiges
      .filter((orderClientLitige) => orderClientLitige.state !== DisputeState.ARCHIVED)
      .reduce((acc: Record<number, number>, orderClientLitige) => {
        orderClientLitige.orderClientLitigeByProducts.forEach((orderClientLitigeByProduct) => {
          acc[orderClientLitigeByProduct.byProduct] = [DisputeState.CLOSED].includes(orderClientLitige.state)
            ? orderClientLitigeByProduct.amount
            : orderClientLitigeByProduct.available;
        });
        return acc;
      }, {});

    return {
      ...document,
      orderDocumentByProducts: document.orderDocumentByProducts.map((orderDocumentByProduct) => ({
        ...orderDocumentByProduct,
        quantity:
          orderDocumentByProduct.quantity - (itemsDispute[orderDocumentByProduct.orderClientByProduct.byProduct] || 0),
      })),
    };
  });

  const optionsDisputeType = (Object.keys(DisputeTypeTranslations) as DisputeType[])
    .filter((disputeType: DisputeType): boolean => {
      switch (disputeType) {
        case DisputeType.OUT_OF_STOCK:
          return !!disputeOrderProducts.length;
        case DisputeType.PROMO_CODE:
        case DisputeType.SHIPPING_FEES:
          return !documents.some((document) =>
            document.orderClientLitiges.some(
              (orderClientLitige) =>
                orderClientLitige.type === disputeType && orderClientLitige.state !== DisputeState.ARCHIVED,
            ),
          );
        case DisputeType.RETURN_TO_SENDER:
        case DisputeType.RETRACTION:
        case DisputeType.PACKAGE_DAMAGED:
        case DisputeType.PACKAGE_LOST:
          return !!disputeInvoiceDocuments.length;
        case DisputeType.PRODUCT_RETRACTION:
        case DisputeType.PRODUCT_DAMAGED:
        case DisputeType.NON_COMPLIANT_PRODUCT:
        case DisputeType.MISSING_PRODUCT:
        case DisputeType.WRONG_ITEM:
          return disputeProductDocuments.some((document) =>
            document.orderDocumentByProducts.some((orderDocumentByProduct) => orderDocumentByProduct.quantity > 0),
          );
        case DisputeType.OTHER:
        case DisputeType.BUG_LABEL:
          return true;
        default:
          return false;
      }
    })
    .map((disputeType) => ({
      value: disputeType,
      label: DisputeTypeTranslations[disputeType],
    }));

  const getFormByType = (): React.ReactNode => {
    switch (type) {
      case DisputeType.OUT_OF_STOCK:
        return (
          <DisputeOrder onSubmit={onSubmit} products={disputeOrderProducts} onClose={onClose} loading={loadingSubmit} />
        );
      case DisputeType.RETURN_TO_SENDER:
      case DisputeType.RETRACTION:
      case DisputeType.PACKAGE_DAMAGED:
      case DisputeType.PACKAGE_LOST:
        return (
          <DisputeInvoice
            onSubmit={onSubmit}
            documents={disputeInvoiceDocuments}
            onClose={onClose}
            loading={loadingSubmit}
          />
        );
      case DisputeType.SHIPPING_FEES:
      case DisputeType.BUG_LABEL:
      case DisputeType.OTHER:
      case DisputeType.PROMO_CODE:
        return <DisputeInvoice onSubmit={onSubmit} documents={documents} onClose={onClose} loading={loadingSubmit} />;
      case DisputeType.PRODUCT_RETRACTION:
      case DisputeType.PRODUCT_DAMAGED:
      case DisputeType.NON_COMPLIANT_PRODUCT:
      case DisputeType.MISSING_PRODUCT:
      case DisputeType.WRONG_ITEM:
        return (
          <DisputeProduct
            onSubmit={onSubmit}
            documents={disputeProductDocuments}
            onClose={onClose}
            loading={loadingSubmit}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Dialog open maxWidth="xl" onClose={onClose}>
      <DialogTitle className={classes.title}>Créer un litige</DialogTitle>
      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          {loading ? (
            <CircularProgress size={20} />
          ) : optionsDisputeType.length ? (
            <Grid size={12}>
              <SelectInput
                id="type"
                label="Type *"
                textHelper="Choisissez un type de litige."
                onChange={(event) => setType(event.target.value as DisputeType)}
                options={optionsDisputeType}
              />
            </Grid>
          ) : (
            <Grid container size={12} alignItems="center" justifyContent="space-between" spacing={2}>
              <Grid>Impossible de créer plus de litige pour cette commande</Grid>
              <ButtonComponent color="#5E6E82" label="Annuler" onClick={onClose} loading={loadingSubmit} />
            </Grid>
          )}

          {getFormByType()}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeStyles({
  title: {
    fontSize: 15,
  },
  content: {
    minWidth: '30vw',
    maxWidth: 900,
  },
});

export default DisputeModal;
